import React from "react";
import ReactDOM from "react-dom/client";
import "./components/css/index.css";

import { ReactKeycloakProvider } from "@react-keycloak/web";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import SessionExpiredAlert from "./components/elements/SessionExpiredAlert";
import { FormDataProvider } from "./helper/FormDataProvider";
import PrivateRoute from "./helper/PrivateRoute";
import { keycloak } from "./Keycloak";
import * as Sentry from "@sentry/react";
import reportWebVitals from "./reportWebVitals";
import { ErrorProvider } from "./services/ErrorProvider";
import { LogoutProvider } from "./services/LogoutProvider";
import { RoleProvider } from "./services/RoleProvider";
import { ServerDownProvider } from "./services/ServerDownProvider";
import { StorageProvider } from "./services/StorageProvider";
import { getServiceUrls } from "./services/serviceUrls";
import NotificationHandler from "./components/elements/NotificationHandler";
import NoNetworkStatusModal from "./components/elements/NoNetworkStatusModal";
import ServerDownHandler from "./components/elements/ServerDownHandler";
import { BrowserRouter } from "react-router-dom";
import { LOCALHOST_HOSTNAMES } from "./utils/constants";

// Validate configurations
const validateConfig = (config) => {
  const requiredKeys = [
    "FEE_SERVICE",
    "USER_SERVICE",
    "TRIP_SERVICE",
    "FBO_SERVICE",
    "MESSAGING_SERVICE",
    "REACT_APP_KEYCLOAK_URL",
    "REACT_APP_KEYCLOAK_REALM",
    "REACT_APP_KEYCLOAK_CLIENT_ID",
  ];
  requiredKeys.forEach((key) => {
    if (!config[key]) {
      throw new Error(`Missing configuration value for: ${key}`);
    }
  });
};

if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then((registration) => {})
    .catch((error) => {
      console.error("Service Worker registration failed:", error);
    });
}

// Initialize Sentry with dynamic configuration
const initializeSentry = () => {
  const config = getServiceUrls();
  const sentryDsn = config.REACT_APP_SENTRY_DSN;

  const isLocalhost =
    LOCALHOST_HOSTNAMES.includes(window.location.hostname) ||
    window.location.hostname.includes("localhost");

  // Only initialize Sentry if not on localhost
  if (!isLocalhost && sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [],
      tracesSampleRate: 1.0,
      autoSessionTracking: true,
      environment: config.REACT_APP_PROFILE_ENV || "dev",
    });
  }
};

// Initialize Sentry early
initializeSentry();

const root = ReactDOM.createRoot(document.getElementById("root"));

const renderApp = () => {
  const queryClient = new QueryClient();
  const keycloakProviderInitConfig = {
    onLoad: "check-sso",
  };
  const kc = keycloak();
  const currentPath = window.location.pathname;
  if (currentPath === "/health") {
    // Import and render health check directly without authentication
    import("./components/elements/HealthCheck").then(
      ({ default: HealthCheck }) => {
        root.render(<HealthCheck />);
      }
    );
    return;
  }

  root.render(
    <ReactKeycloakProvider
      authClient={kc}
      initOptions={keycloakProviderInitConfig}
    >
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <ErrorProvider>
            <ServerDownProvider>
              <LogoutProvider>
                <SessionExpiredAlert />
                <RoleProvider>
                  <NoNetworkStatusModal />
                  <ServerDownHandler />
                  <StorageProvider>
                    <FormDataProvider>
                      <NotificationHandler />
                      <BrowserRouter>
                        <PrivateRoute component={App} />
                      </BrowserRouter>
                    </FormDataProvider>
                  </StorageProvider>
                </RoleProvider>
              </LogoutProvider>
            </ServerDownProvider>
          </ErrorProvider>
        </QueryClientProvider>
      </React.StrictMode>
    </ReactKeycloakProvider>
  );
};
try {
  const config = getServiceUrls();
  validateConfig(config);
  renderApp();
} catch (error) {
  console.error("Configuration validation failed:", error.message);
  root.render(
    <div style={{ textAlign: "center", marginTop: "20%" }}>
      <h1>Configuration Error</h1>
      <p>{error.message}</p>
      <p>Please contact the administrator or try refreshing the page.</p>
    </div>
  );
}
reportWebVitals();
